/**
 * RunImplementationModal.jsx
 *
 * "Run Implementation" modal — shows configuration recap and readiness checks,
 * then triggers the AI agent via the chat event bus.
 * Matches the screenshots showing the green "All checks passed" banner.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { buildImplementationPrompt, sendImplementationPrompt } from '../../utils/dlcPromptBuilder';
import './ConfigureImplementationModal.css'; // reuse shared modal styles
import './RunImplementationModal.css';

export default function RunImplementationModal({ requirement, onClose, onRunStarted }) {
  const [running, setRunning] = useState(false);

  const agentConfig = requirement?.agentConfig || {};
  const swiId  = requirement?.swiId || agentConfig.swiId || '';
  const swiName  = agentConfig.swiName || '';
  const agentName = agentConfig.agentName || '';
  const designMd  = agentConfig.designMd || '';

  // Determine readiness
  const checks = [
    { label: 'Software Item linked', done: !!swiId },
    { label: 'AI Agent selected',    done: !!agentName },
    { label: 'design.md present in SWI', done: designMd.trim().length > 0 },
  ];
  const allPassed = checks.every(c => c.done);

  const handleRun = async () => {
    setRunning(true);
    try {
      const prompt = buildImplementationPrompt(requirement, { agentName, swiId, swiName, designMd });
      
      if (onRunStarted) {
        await onRunStarted();
      }
      
      // Close modal first so user can see the chat start
      onClose();
      
      // Then send the prompt to chat
      sendImplementationPrompt(prompt, true);
    } catch {
      setRunning(false);
    }
  };

  return createPortal(
    <div className="dlc-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dlc-modal run-modal">
        {/* Header */}
        <div className="dlc-modal-header">
          <div className="dlc-modal-icon run-icon">▶</div>
          <div className="dlc-modal-title-group">
            <div className="dlc-modal-title">Run Implementation</div>
            {requirement && (
              <div className="dlc-modal-subtitle">
                {requirement.id} — {requirement.title}
              </div>
            )}
          </div>
          <button className="dlc-modal-close" onClick={onClose} type="button">×</button>
        </div>

        {/* Readiness banner */}
        <div className={`run-readiness-banner ${allPassed ? 'all-passed' : 'has-warnings'}`}>
          <div className="run-readiness-banner-title">
            {allPassed ? '✓ All checks passed — Ready to run' : '⚠ Some checks pending'}
          </div>
          {checks.map(c => (
            <div key={c.label} className={`run-check-item ${c.done ? 'done' : 'pending'}`}>
              <span>{c.done ? '✓' : '○'}</span>
              <span>{c.label}</span>
            </div>
          ))}
        </div>

        {/* Configuration recap */}
        <div className="run-recap">
          <div className="run-recap-title">CONFIGURATION RECAP</div>

          {swiId && (
            <div className="run-recap-card">
              <div className="run-recap-card-label">Software Item</div>
              <div className="run-recap-card-value">
                <span className="run-recap-swi">{swiId}</span>
                {swiName && <span className="run-recap-swi-name"> — {swiName}</span>}
              </div>
              {agentConfig.designMd && (
                <div className="run-recap-design-tag">design.md present — view</div>
              )}
            </div>
          )}

          {agentName && (
            <div className="run-recap-card">
              <div className="run-recap-card-label">AI Agent</div>
              <div className="run-recap-card-value">
                <span className="run-recap-agent">{agentName}</span>
              </div>
              {agentConfig.agentId && (
                <div className="run-recap-agent-meta">{agentConfig.agentId}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="dlc-modal-footer">
          <button className="dlc-btn dlc-btn-secondary" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="dlc-btn dlc-btn-run"
            disabled={running}
            onClick={handleRun}
            type="button"
          >
            {running ? 'Starting…' : '⚡ Run Agent'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

RunImplementationModal.propTypes = {
  requirement: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onRunStarted: PropTypes.func,
};
